import {
  type AdminCoachLeave,
  type AdminCoachRow,
  type AdminCoachUpdateParam,
} from "api/adminApi";
import {
  daysBetweenInputValues,
  shiftDateInputValue,
} from "pages/admin/user/userFormat";

// yyyy-MM-dd → "10월 7일"
export const monthDayLabel = (date: string) =>
  `${Number(date.slice(5, 7))}월 ${Number(date.slice(8, 10))}일`;

// 휴가 중 / 휴가 예정. 목록은 끝나지 않은 휴가만 오므로 지난 휴가는 없다
export type LeaveState = "on" | "planned";

export const leaveStateOf = (leave: AdminCoachLeave, today: string) =>
  (leave.leaveFromDate <= today ? "on" : "planned") as LeaveState;

// 공지일 11시에 배정 중단, 18시에 내담자 안내·연장이 나간다. 안내 전(공지일 18시 전)까지만 고치거나 지울 수 있다.
// hour 는 지금 시(0~23)
export const canEditLeave = (
  leave: AdminCoachLeave,
  today: string,
  hour: number
) =>
  leave.announcementDate > today ||
  (leave.announcementDate === today && hour < 18);

// 공지일 11시 크론이 이미 일일 캡을 0 으로 내린 휴가: 지우거나 공지일을 옮기면 서버가 일일 캡을 50 으로 되돌린다
export const pausedByLeave = (
  leave: AdminCoachLeave,
  today: string,
  hour: number
) => leave.announcementDate === today && hour >= 11;

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

// 공지 시각: "10월 6일(화) 18시". 안내 채팅이 나가는 시각이라 눈에 띄게 보여준다
export const announcementLabel = (date: string) => {
  const weekday = new Date(
    Number(date.slice(0, 4)),
    Number(date.slice(5, 7)) - 1,
    Number(date.slice(8, 10))
  ).getDay();
  return `${monthDayLabel(date)}(${WEEKDAYS[weekday]}) 18시`;
};

// 담당 인원 ÷ 전체 캡 (%) 과 남은 자리. 캡이 0 이면 자동 배정을 안 받는 코치라 비율이 없다
export const capLoad = (assigned: number, cap: number) => ({
  percent: cap > 0 ? Math.round((assigned / cap) * 100) : null,
  left: cap - assigned,
});

// 상세 모달 폼: 서버 coach/update 저장 단위와 같다
export type CoachForm = {
  assignCapCount: number;
  assignDailyCapCount: number;
  isMasterPlanExcluded: boolean;
  isActive: boolean;
  // 계약 기간 (yyyy-MM-dd, 빈 칸 = 없음)
  startDate: string;
  endDate: string;
};

export const coachFormOf = (row: AdminCoachRow): CoachForm => ({
  assignCapCount: row.assignCapCount,
  assignDailyCapCount: row.assignDailyCapCount,
  isMasterPlanExcluded: row.isMasterPlanExcluded,
  isActive: row.isActive,
  startDate: row.startDate ?? "",
  endDate: row.endDate ?? "",
});

type FormField = {
  key: keyof CoachForm;
  label: string;
  // 바뀌면 저장 전에 확인 화면을 띄운다
  important: boolean;
  format: (value: CoachForm[keyof CoachForm]) => string;
};

export const COACH_FORM_FIELDS: FormField[] = [
  {
    key: "assignCapCount",
    label: "전체 캡",
    important: false,
    format: (v) => `${v}`,
  },
  {
    key: "assignDailyCapCount",
    label: "일일 캡",
    important: false,
    format: (v) => `${v}`,
  },
  {
    key: "isMasterPlanExcluded",
    label: "마스터 플랜",
    important: true,
    format: (v) => (v ? "미담당" : "담당"),
  },
  {
    key: "isActive",
    label: "상태",
    important: true,
    format: (v) => (v ? "활성" : "비활성"),
  },
  {
    key: "startDate",
    label: "계약 시작일",
    important: false,
    format: (v) => `${v || "없음"}`,
  },
  {
    key: "endDate",
    label: "계약 종료일",
    important: false,
    format: (v) => `${v || "없음"}`,
  },
];

export const changedFields = (row: AdminCoachRow, form: CoachForm) => {
  const before = coachFormOf(row);
  return COACH_FORM_FIELDS.filter((f) => before[f.key] !== form[f.key]);
};

// 계약 기간 입력 오류: 종료일은 시작일과 같거나 그 뒤 (서버도 같은 규칙으로 막는다)
export const contractPeriodError = (form: CoachForm) =>
  form.startDate && form.endDate && form.endDate < form.startDate
    ? "종료일은 시작일과 같거나 그 뒤여야 해요"
    : null;

// 저장 요청: 바뀐 항목만 보낸다. 계약 기간은 둘 중 하나라도 바뀌면 둘 다 보낸다 (빈 칸 = 없음)
export const coachUpdateParamOf = (
  row: AdminCoachRow,
  form: CoachForm
): AdminCoachUpdateParam => {
  const keys = changedFields(row, form).map((f) => f.key);
  const isPeriodChanged =
    keys.includes("startDate") || keys.includes("endDate");
  return {
    coachId: row.coachId,
    ...Object.fromEntries(
      keys
        .filter((key) => key !== "startDate" && key !== "endDate")
        .map((key) => [key, form[key]])
    ),
    ...(isPeriodChanged && {
      contractPeriod: {
        startDate: form.startDate || null,
        endDate: form.endDate || null,
      },
    }),
  };
};

// 저장 전 확인 화면의 안내: 바뀐 값이 실제 배정에 미치는 영향. 하나라도 있으면 확인 화면을 거친다
export const settingWarnings = (row: AdminCoachRow, form: CoachForm) => {
  const before = coachFormOf(row);
  const warnings: string[] = [];
  if (before.isActive && !form.isActive)
    warnings.push(
      `자동 배정·재배정 후보에서 빠져요. 지금 담당 중인 내담자 ${row.assignedPatientCount}명은 그대로 이 코치에게 남아 있으니, 필요하면 회원 관리에서 코치를 옮겨 주세요.`
    );
  if (!before.isActive && form.isActive)
    warnings.push(
      "바로 자동 배정 후보에 들어가요. 전체 캡·일일 캡이 0보다 크면 오늘부터 신규 내담자를 받아요."
    );
  if (!before.isMasterPlanExcluded && form.isMasterPlanExcluded)
    warnings.push(
      "새로 결제하는 마스터 플랜 내담자는 이 코치에게 배정되지 않아요. 이미 담당 중인 마스터 플랜 내담자는 그대로예요."
    );
  if (before.isMasterPlanExcluded && !form.isMasterPlanExcluded)
    warnings.push("마스터 플랜 내담자도 이 코치에게 자동 배정돼요.");
  if (
    form.isActive &&
    form.assignCapCount > 0 &&
    form.assignCapCount < row.assignedPatientCount &&
    form.assignCapCount !== before.assignCapCount
  )
    warnings.push(
      `전체 캡(${form.assignCapCount})이 담당 인원(${row.assignedPatientCount})보다 작아요. 담당 인원이 캡 밑으로 내려갈 때까지 신규 배정을 받지 않아요.`
    );
  return warnings;
};

// 휴가 등록 폼: 날짜는 yyyy-MM-dd, 연장 일수는 입력 중 빈칸을 허용한다
export type LeaveForm = {
  leaveFromDate: string;
  leaveToDate: string;
  returnDate: string;
  announcementDate: string;
  bonusDays: string;
};

export const leaveFormOf = (leave: AdminCoachLeave): LeaveForm => ({
  ...leave,
  bonusDays: `${leave.bonusDays}`,
});

export const EMPTY_LEAVE_FORM: LeaveForm = {
  leaveFromDate: "",
  leaveToDate: "",
  returnDate: "",
  announcementDate: "",
  bonusDays: "",
};

// 서버 등록·수정과 같은 규칙. 새 공지일은 내일 이후: 오늘로 잡으면 11시 배정 중단 크론이 이미 지났을 수 있다.
// 수정 중(original)이면 원래 휴가는 겹침 검사에서 빼고, 오늘 공지인 휴가는 공지일을 오늘 그대로 둘 수 있다
export const leaveFormError = (
  form: LeaveForm,
  leaves: AdminCoachLeave[],
  today: string,
  original?: AdminCoachLeave | null
): string | null => {
  const { leaveFromDate: from, leaveToDate: to } = form;
  if (!from || !to || !form.returnDate || !form.announcementDate)
    return "날짜를 모두 입력해 주세요.";
  if (to < from) return "휴가 종료일이 시작일보다 빨라요.";
  if (form.returnDate <= to) return "복귀일은 휴가 종료일 다음이어야 해요.";
  if (form.announcementDate > from)
    return "공지일은 휴가 시작일과 같거나 빨라야 해요.";
  const keepsTodayAnnouncement =
    original?.announcementDate === today && form.announcementDate === today;
  if (form.announcementDate <= today && !keepsTodayAnnouncement)
    return "공지일은 내일 이후여야 해요. 공지일 11시·18시에 배정 중단과 안내가 자동으로 실행돼요.";
  const bonusDays = Number(form.bonusDays);
  if (!Number.isInteger(bonusDays) || bonusDays < 1)
    return "이용 기간 연장은 1일 이상이어야 해요.";
  // 오타로 담당 내담자 전원의 이용 기간이 크게 늘면 되돌릴 수 없다
  if (bonusDays > leaveLength(form))
    return "이용 기간 연장은 휴가 일수 이하로 입력해 주세요.";
  // 배정이 멈추는 구간(공지일 ~ 휴가 종료)끼리 겹치면 안 된다. 앞 휴가가 끝난 다음날 0시에 일일 캡이 50 으로
  // 올라가서, 그때 이미 공지된 뒤 휴가 중에도 신규 배정이 들어온다
  if (
    leaves.some(
      (l) =>
        l.leaveFromDate !== original?.leaveFromDate &&
        l.announcementDate <= to &&
        form.announcementDate <= l.leaveToDate
    )
  )
    return "다른 휴가와 배정 중단 기간(공지일 ~ 휴가 종료)이 겹쳐요. 공지일을 앞 휴가가 끝난 뒤로 잡아 주세요.";
  return null;
};

// 휴가 기간 일수 (시작·종료 포함)
export const leaveLength = (leave: {
  leaveFromDate: string;
  leaveToDate: string;
}) => (daysBetweenInputValues(leave.leaveFromDate, leave.leaveToDate) ?? 0) + 1;

// 실무 코치 요약 카드: 활성 코치 기준
export const rosterSummary = (
  rows: AdminCoachRow[],
  recentDays: number,
  recentStartedCount: number,
  today: string
) => {
  const active = rows.filter((r) => r.isActive);
  const capSum = active.reduce((sum, r) => sum + r.assignCapCount, 0);
  const assignedSum = active.reduce(
    (sum, r) => sum + r.assignedPatientCount,
    0
  );
  const left = capSum - assignedSum;
  // 전체 캡 마감 예상: 남은 자리를 최근 하루 평균 신규 배정으로 나눈다 (종료로 빠지는 인원은 반영하지 않는다)
  const perDay = recentDays > 0 ? recentStartedCount / recentDays : 0;
  const daysToFull = left > 0 && perDay > 0 ? Math.ceil(left / perDay) : null;
  const nextLeaveState = (r: AdminCoachRow) =>
    r.leaves[0] ? leaveStateOf(r.leaves[0], today) : null;
  return {
    activeCount: active.length,
    capSum,
    assignedSum,
    left,
    percent: capSum > 0 ? Math.round((assignedSum / capSum) * 100) : null,
    perDay,
    daysToFull,
    fullDate:
      daysToFull == null ? null : shiftDateInputValue(today, daysToFull),
    capReachedCount: active.filter(
      (r) => r.assignCapCount > 0 && r.assignedPatientCount >= r.assignCapCount
    ).length,
    onLeaveCount: active.filter((r) => nextLeaveState(r) === "on").length,
    leavePlannedCount: active.filter((r) => nextLeaveState(r) === "planned")
      .length,
  };
};

// 이메일 도메인은 고정이라 화면에서는 아이디만 받는다. 전체 이메일을 붙여 넣어도 도메인을 떼고 다시 붙인다
export const ORWELL_EMAIL_DOMAIN = "@orwellhealth.org";

export const orwellEmailOf = (id: string) =>
  `${id
    .trim()
    .toLowerCase()
    .replace(/@orwellhealth\.org$/, "")}${ORWELL_EMAIL_DOMAIN}`;

// 교육 코치 이메일: coach{숫자}@orwellhealth.org. 서버 가입이 코치 역할을 주는 형식과 같다
export const isCoachEmail = (email: string) =>
  /^coach\d+@orwellhealth\.org$/.test(email.trim().toLowerCase());

// 모의 내담자 이메일: user{숫자}@orwellhealth.org
export const isSimulatedPatientEmail = (email: string) =>
  /^user\d+@orwellhealth\.org$/.test(email.trim().toLowerCase());

// 모의 내담자 닉네임: 회원 닉네임 규칙과 같은 12자 이하
export const SIMULATED_NICKNAME_MAX_LENGTH = 12;

// 한 번에 등록할 수 있는 모의 내담자 수 (서버와 같다)
export const SIMULATED_MAX_ROWS = 10;

// 모의 내담자 등록 폼 한 줄: 이메일 아이디(도메인 고정) + 닉네임
export type SimulatedPatientFormRow = { emailId: string; nickname: string };

// 줄마다 오류. 빈 칸은 오류로 띄우지 않는다 (제출만 막는다). 같은 이메일은 뒤의 줄에 표시한다
export const simulatedRowErrors = (rows: SimulatedPatientFormRow[]) => {
  const emails = rows.map((row) => orwellEmailOf(row.emailId));
  return rows.map((row, i) => ({
    email:
      row.emailId.trim() === ""
        ? null
        : !isSimulatedPatientEmail(emails[i])
          ? "user{숫자} 형식으로 입력해 주세요"
          : emails.indexOf(emails[i]) !== i
            ? "위에서 입력한 이메일이에요"
            : null,
    nickname:
      row.nickname.trim().length > SIMULATED_NICKNAME_MAX_LENGTH
        ? `${SIMULATED_NICKNAME_MAX_LENGTH}자 이하로 입력해 주세요`
        : null,
  }));
};

// 모든 줄이 채워져 있고 오류가 없어야 등록할 수 있다
export const canSubmitSimulatedRows = (rows: SimulatedPatientFormRow[]) =>
  rows.length > 0 &&
  rows.length <= SIMULATED_MAX_ROWS &&
  rows.every(
    (row) => row.emailId.trim() !== "" && row.nickname.trim() !== ""
  ) &&
  simulatedRowErrors(rows).every((e) => e.email == null && e.nickname == null);
